import { useEffect, useRef } from 'react';

import { isBrowser } from 'lib/utils';

import INTENT_BANNER from 'constants/intentBanner';

/**
 * First-intent trigger.
 *
 * - Desktop: fires `desktopDelayMs` (default 3s) after mount (home load).
 * - Mobile: fires on scroll past `mobileMinScrollPx` (default 100px) OR after
 *   `mobileDelayMs` (default 4s), whichever comes first.
 *
 * SSR-safe (guards window) and lazy (no work until `enabled`). Fires once.
 *
 * @param {object} params
 * @param {boolean} params.enabled - gate (feature flag + frequency + has data)
 * @param {boolean} params.isMobile
 * @param {function} params.onTrigger
 * @param {object} [params.config] - smartbanner_obj.intent.first
 */
const useFirstIntent = ({ enabled, isMobile, onTrigger, config = {} }) => {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isBrowser()) {
      // Re-arm when disabled so a new eligibility window (e.g. a swapped
      // campaign whose session cap reset) can fire again.
      firedRef.current = false;
      return undefined;
    }

    const {
      desktopDelayMs = INTENT_BANNER.DEFAULTS.FIRST.desktopDelayMs,
      mobileMinScrollPx = INTENT_BANNER.DEFAULTS.FIRST.mobileMinScrollPx,
      mobileDelayMs = INTENT_BANNER.DEFAULTS.FIRST.mobileDelayMs,
    } = config;

    let timeoutId;

    const fire = () => {
      if (firedRef.current) {
        return;
      }

      firedRef.current = true;
      cleanup();
      onTrigger();
    };

    const onScroll = () => {
      if (window.scrollY > mobileMinScrollPx) {
        fire();
      }
    };

    function cleanup() {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      window.removeEventListener('scroll', onScroll);
    }

    if (isMobile) {
      timeoutId = setTimeout(fire, mobileDelayMs);
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      timeoutId = setTimeout(fire, desktopDelayMs);
    }

    return cleanup;
  }, [enabled, isMobile, onTrigger, config]);
};

export default useFirstIntent;
